// src/components/TestimonialCard.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { 
  Star, 
  Quote, 
  TrendingUp,
  Award,
  Calendar
} from 'lucide-react'

const TestimonialCard = ({ testimonial, index = 0 }) => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating 
            ? 'text-yellow-400 fill-current' 
            : 'text-gray-300'
        }`}
      />
    ))
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:scale-105 relative overflow-hidden">
        {/* Quote Icon */}
        <div className="absolute top-4 right-4 opacity-5">
          <Quote className="w-16 h-16 text-blue-600" />
        </div>

        {/* Header */}
        <div className="flex items-start space-x-4 mb-4">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg">
              {testimonial.user?.username?.[0]?.toUpperCase() || 'U'}
            </div>
          </div>

          {/* User Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-bold text-gray-900 dark:text-white truncate">
                {testimonial.user?.username || 'Anonymous User'}
              </h4>
              <div className="flex items-center space-x-1">
                {renderStars(testimonial.rating || 5)}
              </div>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <Award className="w-3 h-3" />
              <span>Level {testimonial.user?.level || 'Pro'}</span>
            </div>

            {/* Date */}
            <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-500 mt-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(testimonial.submittedAt || new Date())}</span>
            </div>
          </div>
        </div>

        {/* Testimonial Content */}
        <div className="mb-4">
          <h5 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-1">
            {testimonial.subject}
          </h5>
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed line-clamp-4">
            {testimonial.message}
          </p>
        </div>

        {/* Investment Stats (if available) */}
        {(testimonial.investment || testimonial.returns) && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-3 mb-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Invested</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">
                  {testimonial.investment || '$5,000'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Returns</div>
                <div className="text-sm font-bold text-green-600 dark:text-green-400">
                  {testimonial.returns || '$6,750'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-500">
            <TrendingUp className="w-3 h-3" />
            <span>Verified Investor</span>
          </div>
          
          {/* Package Type */}
          {testimonial.type && (
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              testimonial.type === 'COMPLIMENT' 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
            }`}>
              {testimonial.type === 'COMPLIMENT' ? 'Positive' : 'Feedback'}
            </div>
          )}
        </div>

        {/* Hover Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    </motion.div>
  )
}

export default TestimonialCard